"use client"


import { useTheme } from "next-themes"
import Earth from "./ui/globe"
import ScrambleHover from "./ui/scramble"
import { FollowerPointerCard } from "./ui/following-pointer"
import { motion, useInView } from "framer-motion"
import { Suspense, useEffect, useRef, useState } from "react"
import { geist } from "@/lib/fonts"
import { Feather, Layout } from "lucide-react"


export default function Features() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const { theme } = useTheme()
  const [isCampaignsHovering, setIsCampaignsHovering] = useState(false)
  const [isAnalyticsHovering, setIsAnalyticsHovering] = useState(false)
  const [isSmartLinksHovering, setIsSmartLinksHovering] = useState(false)
  const [isReferralsHovering, setIsReferralsHovering] = useState(false)

  const [inputValue, setInputValue] = useState("")

  const [baseColor, setBaseColor] = useState([0.906, 0.541, 0.325]) // #e78a53 in RGB normalized
  const [glowColor, setGlowColor] = useState([0.906, 0.541, 0.325]) // #e78a53 in RGB normalized

  const [dark, setDark] = useState(theme === "dark" ? 1 : 0)

  useEffect(() => {
    setBaseColor([0.906, 0.541, 0.325]) // #e78a53
    setGlowColor([0.906, 0.541, 0.325]) // #e78a53
    setDark(theme === "dark" ? 1 : 0)
  }, [theme])

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      setInputValue("")
    }
  }

  return (
    <section id="features" className="text-foreground relative overflow-hidden py-12 sm:py-24 md:py-32">
      {/* Glowing radial background behind Features */}
      <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#a78bfa]/30 via-[#a78bfa]/10 to-transparent opacity-60 blur-2xl"></div>
      {/* Animated glowing border above Features */}
      <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-[2px]">
        <div className="w-full h-full bg-gradient-to-r from-transparent via-[#e3c5b3] to-transparent animate-pulse" style={{ opacity: 0.5 }} />
      </div>
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.5, delay: 0 }}
        className="container mx-auto flex flex-col items-center gap-6 sm:gap-12"
      >
        					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5 }}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
					>
						<Layout className="w-4 h-4 text-[#e3c5b3]" />
						<span className="text-sm font-medium text-white/80">Features</span>
					</motion.div>
        <h2
          className={["mb-5 text-center text-4xl font-semibold tracking-tighter text-white md:text-[54px] md:leading-[60px] relative z-10", geist.className].filter(Boolean).join(" ")}
          style={{
            textShadow: '0 0 16px #e3c5b3, 0 0 4px #fff',
          }}
        >
          Experience the power of Amply
        </h2>
        <FollowerPointerCard
          title={
            <div className="flex items-center gap-2">
              <span>✨</span>
              <span>Interactive Features</span>
            </div>
          }
        >
          <div className="cursor-none">
            <div className="grid grid-cols-12 gap-4 justify-center">
           <motion.div
        className="group border-secondary/40 text-card-foreground relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-6 xl:col-start-2"
        onMouseEnter={() => setIsCampaignsHovering(true)}
        onMouseLeave={() => setIsCampaignsHovering(false)}
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        whileHover={{
          scale: 1.02,
          borderColor: "#3b82f6",
          boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)",
        }}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-2xl leading-none font-semibold tracking-tight">Campaign Management</h3>
          <div className="text-md text-muted-foreground flex flex-col gap-2 text-sm">
            <p className="max-w-[460px]">
              Create, launch, and optimize marketing campaigns with intelligent automation and real-time performance
              tracking.
            </p>
            <br/>
          </div>
        </div>
        <div className="pointer-events-none flex grow items-center justify-center select-none relative min-h-[300px]">
          <div className="relative w-full h-[300px] rounded-xl overflow-hidden bg-gradient-to-br from-blue-500/10 to-purple-600/10">
            {/* Campaign Cards Animation */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                {["Email Campaign", "Social Media", "PPC Ads", "Content Marketing"].map((campaign, index) => (
                  <motion.div
                    key={campaign}
                    className="bg-white/90 dark:bg-black rounded-lg p-3 shadow-lg backdrop-blur-sm"
                    
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={
                      isCampaignsHovering
                        ? {
                            opacity: 1,
                            scale: 1,
                            y: [0, -5, 0],
                          }
                        : { opacity: 0.7, scale: 0.9 }
                    }
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      y: { duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" },
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{campaign}</span>
                    </div>
                    <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-blue-500 rounded-full"
                        initial={{ width: "0%" }}
                        animate={isCampaignsHovering ? { width: `${60 + index * 10}%` } : { width: "20%" }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Animated Connection Lines */}
            <motion.div
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={isCampaignsHovering ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <svg width="100%" height="100%" className="absolute">
                <motion.path
                  d="M 50 50 Q 150 100 250 50 Q 350 100 450 50"
                  stroke="rgb(59, 130, 246)"
                  strokeWidth="2"
                  fill="transparent"
                  strokeDasharray="4 4"
                  initial={{ pathLength: 0 }}
                  animate={isCampaignsHovering ? { pathLength: 1 } : { pathLength: 0 }}
                  transition={{ duration: 2, ease: "easeInOut" }}
                />
              </svg>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Analytics Dashboard */}
      <motion.div
        className="group border-secondary/40 text-card-foreground relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-6 xl:col-start-8"
        onMouseEnter={() => setIsAnalyticsHovering(true)}
        onMouseLeave={() => setIsAnalyticsHovering(false)}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        whileHover={{
          scale: 1.02,
          borderColor: "#10b981",
          boxShadow: "0 0 30px rgba(16, 185, 129, 0.2)",
        }}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-2xl leading-none font-semibold tracking-tight">Advanced Analytics</h3>
          <div className="text-md text-muted-foreground flex flex-col gap-2 text-sm">
            <p className="max-w-[460px]">
              Deep insights into user behavior, conversion rates, and ROI with beautiful, interactive dashboards.
            </p>
          </div>
        </div>
        <div className="flex grow items-center justify-center select-none relative min-h-[300px]">
          <div className="relative w-full max-w-sm bg-transparent rounded-xl p-4">
            {/* Analytics Chart */}
            <div className="bg-white/90 dark:bg-black rounded-lg p-4 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Performance</span>
                <span className="text-xs text-emerald-600 font-medium">+24.5%</span>
              </div>

              <div className="flex items-end gap-1 h-20">
                {[40, 65, 45, 80, 55, 90, 70].map((height, index) => (
                  <motion.div
                    key={index}
                    className="bg-emerald-500 rounded-sm flex-1"
                    initial={{ height: 0 }}
                    animate={isAnalyticsHovering ? { height: `${height}%` } : { height: "20%" }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  />
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                {[
                  { label: "Clicks", value: "12.4K", color: "text-blue-600" },
                  { label: "Conversions", value: "2.1K", color: "text-emerald-600" },
                  { label: "Revenue", value: "$45.2K", color: "text-purple-600" },
                ].map((metric, index) => (
                  <motion.div
                    key={metric.label}
                    className="text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={isAnalyticsHovering ? { opacity: 1, y: 0 } : { opacity: 0.7, y: 5 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className={`text-xs font-bold ${metric.color}`}>{metric.value}</div>
                    <div className="text-xs text-gray-500">{metric.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Smart Links */}
      <motion.div
        className="group border-secondary/40 text-card-foreground relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-6 xl:col-start-2"
        onMouseEnter={() => setIsSmartLinksHovering(true)}
        onMouseLeave={() => setIsSmartLinksHovering(false)}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        whileHover={{
          borderColor: "#9353E7",
        }}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-2xl leading-none font-semibold tracking-tight">Smart Links</h3>
          <div className="text-md text-muted-foreground flex flex-col gap-2 text-sm">
            <p className="max-w-[460px]">
              Intelligent link management with dynamic routing, A/B testing, and geo-targeting for maximum engagement.
            </p>
          </div>
        </div>
        <div className="flex grow items-center justify-center select-none relative min-h-[300px]">
          <div className="relative w-full max-w-sm">
            {/* Smart Link Interface */}
            <div style={{ borderRadius: '999px', background: "transparent" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Smart Link Builder</span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <span className="text-xs text-gray-500">🔗</span>
                  <span className="text-xs font-mono text-gray-600 dark:text-gray-400">smartlink.co/promo</span>
                </div>

                <motion.div
                  className="space-y-2"
                  initial={{ opacity: 0 }}
                  animate={isSmartLinksHovering ? { opacity: 1 } : { opacity: 0.7 }}
                  transition={{ duration: 0.5 }}
                >
                  {["🌍 Geo-targeting: US, UK, CA", "📱 Device: Mobile optimized", "🎯 A/B Test: Version A"].map(
                    (feature, index) => (
                      <motion.div
                        key={feature}
                        className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400"
                        initial={{ x: -20, opacity: 0 }}
                        animate={isSmartLinksHovering ? { x: 0, opacity: 1 } : { x: -10, opacity: 0.5 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <span>{feature}</span>
                      </motion.div>
                    ),
                  )}
                </motion.div>

                <motion.div
                  className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
                  animate={isSmartLinksHovering ? { scale: [1, 1.02, 1] } : { scale: 1 }}
                  transition={{ duration: 1, repeat: isSmartLinksHovering ? Number.POSITIVE_INFINITY : 0 }}
                >
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Click Rate</span>
                  <span className="text-xs font-bold text-amber-600">+127%</span>
                </motion.div>
              </div>
            </div>

            {/* Floating Link Nodes */}
            <motion.div
              className="absolute -top-4 -right-4 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs"
              animate={
                isSmartLinksHovering
                  ? {
                      y: [0, -10, 0],
                      rotate: [0, 180, 360],
                    }
                  : { y: 0, rotate: 0 }
              }
              transition={{ duration: 2, repeat: isSmartLinksHovering ? Number.POSITIVE_INFINITY : 0 }}
            >
              🔗
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Referral System */}
      <motion.div
        className="group border-secondary/40 text-card-foreground relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-6 xl:col-start-8"
        onMouseEnter={() => setIsReferralsHovering(true)}
        onMouseLeave={() => setIsReferralsHovering(false)}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        whileHover={{
          scale: 1.02,
          borderColor: "#8b5cf6",
          boxShadow: "0 0 30px rgba(139, 92, 246, 0.2)",
        }}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-2xl leading-none font-semibold tracking-tight">Referral Engine</h3>
          <div className="text-md text-muted-foreground flex flex-col gap-2 text-sm">
            <p className="max-w-[460px]">
              Powerful referral system with automated rewards, multi-tier commissions, and viral growth mechanics.
            </p>
          </div>
        </div>
        <div className="flex grow items-center justify-center select-none relative min-h-[300px]">
          <div className="relative w-full max-w-sm">
            {/* Referral Network Visualization */}
            <div className="relative bg-gradient-to-br from-[#e3c5b3]/10 to-[#e3c5b3]/10 rounded-xl p-6">
              {/* Central User */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <motion.div
                  className="w-12 h-12 bg-[#e3c5b3] rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                  animate={isReferralsHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                  transition={{ duration: 1, repeat: isReferralsHovering ? Number.POSITIVE_INFINITY : 0 }}
                >
                  👤
                </motion.div>
              </div>

              {/* Referral Connections */}
              {[0, 1, 2, 3, 4, 5].map((index) => {
                const angle = index * 60 * (Math.PI / 180)
                const radius = 60
                const x = Math.cos(angle) * radius
                const y = Math.sin(angle) * radius

                return (
                  <motion.div
                    key={index}
                    className="absolute top-1/2 left-1/2 w-8 h-8 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-md border-2 border-purple-300"
                    style={{
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={
                      isReferralsHovering
                        ? {
                            opacity: 1,
                            scale: 1,
                            rotate: [0, 360],
                          }
                        : { opacity: 0.7, scale: 0.8, rotate: 0 }
                    }
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      rotate: {
                        duration: 3,
                        repeat: isReferralsHovering ? Number.POSITIVE_INFINITY : 0,
                        ease: "linear",
                      },
                    }}
                  >
                    <span className="text-xs">👥</span>
                  </motion.div>
                )
              })}

              {/* Connection Lines */}
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={isReferralsHovering ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <svg width="100%" height="100%" className="absolute">
                  {[0, 1, 2, 3, 4, 5].map((index) => {
                    const angle = index * 60 * (Math.PI / 180)
                    const radius = 60
                    const x = Math.cos(angle) * radius + 120
                    const y = Math.sin(angle) * radius + 120

                    return (
                      <motion.line
                        key={index}
                        x1="120"
                        y1="120"
                        x2={x}
                        y2={y}
                        stroke="#e3c5b3"
                        strokeWidth="2"
                        strokeDasharray="3 3"
                        initial={{ pathLength: 0 }}
                        animate={isReferralsHovering ? { pathLength: 1 } : { pathLength: 0 }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                      />
                    )
                  })}
                </svg>
              </motion.div>

              {/* Rewards Counter */}
              <motion.div
              style={{ background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000", }}
                className="absolute bottom-4 left-4 rounded-lg p-2 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={isReferralsHovering ? { opacity: 1, y: 0 } : { opacity: 0.7, y: 10 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-xs font-medium text-[#e3c5b3]">Rewards Earned</div>
                <motion.div
                  className="text-lg font-bold text-[#e3c5b3] dark:text-orange-400"
                  animate={isReferralsHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                  transition={{ duration: 0.8, repeat: isReferralsHovering ? Number.POSITIVE_INFINITY : 0 }}
                >
                  $2,450
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
            </div>
          </div>
        </FollowerPointerCard>
      </motion.div>
    </section>
  )
}